import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { equals } from 'ramda';

import { BASE_ROOT, extractFromQueryString } from '@ringcentral/rwc-utils/src/url';
import { FEATURE_FLAGS } from '@ringcentral/rwc-utils/src/constants/feature-flags';
import { featureFlagClient } from '@ringcentral/rwc-utils/src/feature-flag-client';
import getConfig from '@ringcentral/rwc-utils/src/config';
import { getNavigator, isFF } from '@ringcentral/rwc-utils/src/client-detection';
import { shortToLongLocales } from '@ringcentral/rwc-utils/src/constants/locales';
import { writeLocallyStored, readLocallyStored } from '@ringcentral/rwc-utils/src/local-storage';
import { removeFromQuery } from '@ringcentral/rwc-utils/src/urls/remove-from-query';
import { FALLBACK_LANG, LOCALES } from '@rwc/constants/white-labeling';
import format from '@rwc/utils/format';
import brandLocales from '@rwc/utils/brand-locales';
import { isDynamicBrand } from '@ringcentral/rwc-branding/src/utils';

const BUILD_HASH = process.env.BUILD_HASH;
const LOCALE_QUERY_NAME = 'pt_localeId';

const getLoadPath = () => {
    const pathToLocale = `${BASE_ROOT}static`;

    return `${pathToLocale.replace(/\/+$/, '')}/locale-{{lng}}.${BUILD_HASH}.json`;
};

let cachedLocale;
const getQueryLocale = () => {
    if (cachedLocale) return [cachedLocale];

    const localeFromQuery = extractFromQueryString(LOCALE_QUERY_NAME);
    if (localeFromQuery) {
        const locale = localeFromQuery.replace('_', '-'); // en_US -> en-US
        writeLocallyStored(LOCALE_QUERY_NAME, locale);
        removeFromQuery(LOCALE_QUERY_NAME, false);
        cachedLocale = locale;
    } else {
        cachedLocale = readLocallyStored(LOCALE_QUERY_NAME, null, false);
    }

    return cachedLocale ? [cachedLocale] : null;
};

const getPreferredLocales = () => {
    const navigator = getNavigator();
    const preferredLanguageList = navigator.languages;
    const preferredLanguage = navigator.language;
    const queryLocale = getQueryLocale();

    return queryLocale || preferredLanguageList || [preferredLanguage];
};

/**
 * Gets the default locale based on either browser preferences, query or brand.
 *
 * Return query locale param, if the query not provided
 * looks at the browser languages in the order of preference
 * and returns the first match supported by the app.
 * Otherwise, returns the default locale for the brand from config.
 *
 * Fallbacks to 'en-US'.
 *
 * TODO: define mappings for selecting default locales when either a short form (e.g. 'en')
 * or an unsupported region is provided.
 *
 * @returns {string} default locale
 */
export const getDefaultLocale = () => {
    const preferredLocales = getPreferredLocales();
    const brandLocalesValues = Object.values(brandLocales);

    for (const preferredLocale of preferredLocales) {
        // Check that preferred locale exists in the list of locales and available for current brand.
        if (LOCALES[preferredLocale] && brandLocales[preferredLocale]) {
            return LOCALES[preferredLocale];
        }

        // Check for the shortened form (for ex. we have 'fr' or 'fr-CA', try to find something what fits to 'fr-*').
        const shortened = preferredLocale.split('-')[0];
        // in FF, Use pt-PT locale as fallback locale for Portuguese
        const longLocale = isFF()
            ? {
                  ...shortToLongLocales,
                  pt: 'pt-PT',
              }[shortened]
            : shortToLongLocales[shortened];
        const brandLang = brandLocalesValues.find(brandLocale => brandLocale === longLocale);
        if (brandLang) {
            return brandLang;
        }
    }

    // Nothing found, use default locale.
    // The fallback brand locale will move to FFS, before that we get it from feature flag for dynamic brand,
    // For partners that have not migrated to dynamic branding, reading the value from config is still sufficient.
    let configLocale = getConfig('LOCALES')?.default;
    if (isDynamicBrand) {
        configLocale = featureFlagClient.getFlag(FEATURE_FLAGS.fallbackLocale)?.locale;
    }

    return getLocaleToUse(configLocale);
};

/**
 * Tries to return given locale if it's available or returns fallback locale.
 */
const getLocaleToUse = localeToCheck =>
    // Check that locale exists in the list of locales and available for current brand.
    (LOCALES[localeToCheck] && brandLocales[localeToCheck]) ?? FALLBACK_LANG;
export const initI18n = async (defaultLocale = getDefaultLocale()) =>
    i18n
        .use(initReactI18next)
        .use(HttpApi)
        .init({
            // Default locale is defined according to navigator and query language @see getDefaultLocale()
            // and according to available languages for current brand @see getLocaleToUse().
            // But it can be overridden by param 'locale' in LocalStorage, as this param is set through
            // the "Diagnostics" tab.
            lng: defaultLocale,
            fallbackLng: FALLBACK_LANG,
            load: 'currentOnly',
            joinArrays: '\n',
            interpolation: {
                format: format,
                formatSeparator: ',',
                defaultVariables: {},
                escapeValue: false,
            },
            debug: process.env.NODE_ENV === 'development',
            backend: {
                loadPath: getLoadPath,
            },
        });

/**
 * set defaultVariables after i18n init
 * @param {*} variables
 */
export const setInterpolationDefaults = variables => {
    let isUpdated = false;
    if (
        i18n?.options?.interpolation &&
        !equals(i18n.options.interpolation.defaultVariables, variables)
    ) {
        i18n.options.interpolation.defaultVariables = {
            ...variables,
        };
        isUpdated = true;
    }

    return isUpdated;
};
